import { useLayoutEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ErrorBoundary, useLocale, useMutation, useQuery, useQueryClient, useClient } from 'shared-ui';
import { useLocation, useParams } from 'react-router';
import { toast } from 'react-toastify';

import {
  fetchUserFunctionalPermissions,
  updateUserFunctionalPermissions,
  fetchUserAccessSettings,
  updateUserAccessSettings,
} from 'api/userPermissions/userPermissions';
import { QueryKeyEnum } from 'core/global.enum';
import { useIsExactPath } from 'hooks/useIsExactPath/useIsExactPath';
import { AppRouteWithParams } from 'routing/AppRoute.enum';
import { fetchOrganizations } from 'api/organizations/organizations';
import { Functionality } from 'api/userPermissions/userPermissions.enum';
import { AppMessages } from 'i18n/messages';
import { useAuth } from 'hooks/useAuth/useAuth';

import { UserPermissions } from './UserPermissions';
import { UserPermissionsForm } from './UserPermissions.types';

export const UserPermissionsContainer = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { formatMessage } = useLocale();
  const { user: currentUser } = useAuth();
  const { setHasPermissionChanged } = useClient();

  const isUserFunctionalPermissionsView = useIsExactPath(AppRouteWithParams.userFunctionalPermissions.path);

  const formMethods = useForm<UserPermissionsForm>({
    mode: 'all',
    defaultValues: {
      permissions: [],
      accessAllData: true,
      accessFeaturedCards: true,
      accessNoOrganizations: true,
      selectedOrganizations: [],
    },
  });

  const createDefaultPermissions = () =>
    Object.values(Functionality).map((functionality) => ({
      functionality,
      viewAction: false,
      modifyAction: false,
    }));

  /**
   * Merge existing permissions with defaults for ALL functionalities.
   * This ensures new functionalities (like reports) are included even if
   * they don't exist in the database yet.
   */
  const mergePermissionsWithDefaults = (existingPermissions: typeof userPermissions) => {
    const defaults = createDefaultPermissions();
    return defaults.map((defaultPerm) => {
      const existing = existingPermissions?.find((p) => p.functionality === defaultPerm.functionality);
      return existing || defaultPerm;
    });
  };

  // For user permissions management, we need to show all organizations (unfiltered) so admins can assign permissions to any organization
  const { data: organizations = [] } = useQuery([QueryKeyEnum.ORGANIZATIONS], () => fetchOrganizations());

  const {
    data: userPermissions,
    isLoading: isLoadingFunctionalPermissions,
    isFetching: isFetchingFunctionalPermissions,
  } = useQuery([QueryKeyEnum.USER_PERMISSIONS, id], () => fetchUserFunctionalPermissions(id || ''), { enabled: !!id });

  const {
    data: userAccessSettings,
    isLoading: isLoadingAccessSettings,
    isFetching: isFetchingAccessSettings,
  } = useQuery([QueryKeyEnum.USER_ACCESS_SETTINGS, id], () => fetchUserAccessSettings(id || ''), { enabled: !!id });

  const { mutate: updatePermissions, isLoading: isUpdatingPermissions } = useMutation(updateUserFunctionalPermissions, {
    onSuccess: (data) => {
      queryClient.invalidateQueries([QueryKeyEnum.USER_PERMISSIONS, id]);
      toast.dark(formatMessage({ id: AppMessages['permissions.success'] }));
    },
  });

  const { mutateAsync: updateUserSettings, isLoading: isUpdatingUserSettings } = useMutation(updateUserAccessSettings, {
    onSuccess: async () => {
      queryClient.invalidateQueries([QueryKeyEnum.USER_ACCESS_SETTINGS, id]);

      // If updating current user's permissions, trigger permission change notification
      // This will show a modal prompting the user to refresh for the changes to take effect
      if (id === currentUser?.id) {
        setHasPermissionChanged(true);
      }

      toast.dark(formatMessage({ id: AppMessages['permissions.success'] }));
    },
  });

  const buildFormData = (): UserPermissionsForm => {
    return {
      permissions: mergePermissionsWithDefaults(userPermissions),
      accessAllData: userAccessSettings?.accessAllData || false,
      accessFeaturedCards: userAccessSettings?.accessFeaturedCards || false,
      accessNoOrganizations: userAccessSettings?.accessNoOrganizations || false,
      selectedOrganizations: userAccessSettings?.dataPermissions?.map((org) => org.organizacjaId) || [],
    };
  };

  const onSubmit = async () => {
    const data = formMethods.watch();

    if (isUserFunctionalPermissionsView) {
      updatePermissions({ userId: id || '', permissions: data.permissions });
    } else {
      await updateUserSettings({
        userId: id || '',
        accessAllData: data.accessAllData,
        accessFeaturedCards: data.accessFeaturedCards,
        accessNoOrganizations: data.accessNoOrganizations,
        permissions: data.selectedOrganizations.map((komorkaId) => ({ komorkaId })),
      });
    }
  };

  const onCancel = () => {
    formMethods.reset(buildFormData());
  };

  useLayoutEffect(() => onCancel(), [location.pathname]);

  useLayoutEffect(() => {
    formMethods.reset(buildFormData());
  }, [userPermissions, userAccessSettings]);

  const isLoading =
    isLoadingFunctionalPermissions ||
    isLoadingAccessSettings ||
    isFetchingFunctionalPermissions ||
    isFetchingAccessSettings;
  const isSaving = isUpdatingPermissions || isUpdatingUserSettings;

  return (
    <ErrorBoundary>
      <FormProvider {...formMethods}>
        <UserPermissions
          onSubmit={onSubmit}
          onCancel={onCancel}
          isLoading={isLoading}
          isSaving={isSaving}
          organizations={organizations}
        />
      </FormProvider>
    </ErrorBoundary>
  );
};
